import { IRing } from "./interfaces";

export type CuratedColorMood =
  | "ocean-cool"
  | "forest-green"
  | "sunset-warm"
  | "romantic-pink"
  | "midnight-dark"
  | "rainbow-playful";

export type CuratedStoneEnergy =
  | "classic-diamond"
  | "color-pop"
  | "dreamy-soft"
  | "ethical-lab"
  | "dark-dramatic";

export type CuratedSilhouette =
  | "classic-timeless"
  | "modern-clean"
  | "antique-romance"
  | "bold-statement"
  | "unique-artistic";

export type CuratedExtraLevel = "clean" | "subtle" | "sparkly" | "statement";
export type CuratedBandPersonality = "sleek" | "twisted-organic" | "vintage-texture" | "geometric-edgy";
export type CuratedMetalTone = "yellow-gold" | "white-gold" | "rose-gold" | "platinum" | "silver";
export type CuratedSizeVibe = "delicate" | "balanced" | "bold";

export type CuratedStackQuiz = {
  stoneColorMood: CuratedColorMood;
  stoneEnergy: CuratedStoneEnergy;
  silhouetteVibes: CuratedSilhouette[];
  shapePreferences: string[];
  extraLevel: CuratedExtraLevel;
  bandPersonality: CuratedBandPersonality;
  metalTone: CuratedMetalTone;
  sizeVibe: CuratedSizeVibe;
};

export const CURATED_STACK_SIZE = 10;
export const CURATED_STACK_TTL_MS = 24 * 60 * 60 * 1000;

export const CURATED_STACK_LOCAL_STORAGE_KEY = "curatedStackV2";
export const CURATED_STACK_ONBOARDING_PENDING_KEY = "curatedStackOnboardingPendingV2";

export type CuratedStackRecordLocal = {
  quiz: CuratedStackQuiz;
  ringIds: string[];
  generatedAt: number;
};

export type CuratedHistoryProfile = {
  likedTags: Record<string, number>;
  dislikedTags: Record<string, number>;
  preferredSizes: number[];
};

export const CURATED_COLOR_MOOD_OPTIONS: { value: CuratedColorMood; label: string }[] = [
  { value: "ocean-cool", label: "Ocean cool" },
  { value: "forest-green", label: "Forest green" },
  { value: "sunset-warm", label: "Sunset warm" },
  { value: "romantic-pink", label: "Romantic pink" },
  { value: "midnight-dark", label: "Midnight dark" },
  { value: "rainbow-playful", label: "Rainbow / playful" },
];

export const CURATED_STONE_ENERGY_OPTIONS: { value: CuratedStoneEnergy; label: string }[] = [
  { value: "classic-diamond", label: "Classic diamond" },
  { value: "color-pop", label: "Color-pop gemstone" },
  { value: "dreamy-soft", label: "Soft dreamy glow" },
  { value: "ethical-lab", label: "Ethical lab-grown" },
  { value: "dark-dramatic", label: "Dark dramatic" },
];

export const CURATED_SILHOUETTE_OPTIONS: { value: CuratedSilhouette; label: string }[] = [
  { value: "classic-timeless", label: "Classic timeless" },
  { value: "modern-clean", label: "Modern clean" },
  { value: "antique-romance", label: "Antique romance" },
  { value: "bold-statement", label: "Bold statement" },
  { value: "unique-artistic", label: "Unique artistic" },
];

export const CURATED_SHAPE_OPTIONS = [
  { value: "round", label: "Round" },
  { value: "oval", label: "Oval" },
  { value: "emerald", label: "Emerald" },
  { value: "cushion", label: "Cushion" },
  { value: "pear", label: "Pear" },
  { value: "princess", label: "Princess" },
  { value: "radiant", label: "Radiant" },
  { value: "asscher", label: "Asscher" },
  { value: "marquise", label: "Marquise" },
  { value: "trillion", label: "Trillion" },
  { value: "hexagon", label: "Hexagon" },
] as const;

export const CURATED_EXTRA_LEVEL_OPTIONS: { value: CuratedExtraLevel; label: string }[] = [
  { value: "clean", label: "Clean solitaire" },
  { value: "subtle", label: "Subtle detail" },
  { value: "sparkly", label: "Sparkly halo" },
  { value: "statement", label: "Statement piece" },
];

export const CURATED_BAND_PERSONALITY_OPTIONS: { value: CuratedBandPersonality; label: string }[] = [
  { value: "sleek", label: "Sleek & simple" },
  { value: "twisted-organic", label: "Twisted / organic" },
  { value: "vintage-texture", label: "Vintage texture" },
  { value: "geometric-edgy", label: "Geometric / edgy" },
];

export const CURATED_METAL_OPTIONS: { value: CuratedMetalTone; label: string }[] = [
  { value: "yellow-gold", label: "Yellow Gold" },
  { value: "white-gold", label: "White Gold" },
  { value: "rose-gold", label: "Rose Gold" },
  { value: "platinum", label: "Platinum" },
  { value: "silver", label: "Silver" },
];

export const CURATED_SIZE_VIBE_OPTIONS: { value: CuratedSizeVibe; label: string }[] = [
  { value: "delicate", label: "Delicate" },
  { value: "balanced", label: "Balanced" },
  { value: "bold", label: "Bold" },
];

export const DEFAULT_CURATED_QUIZ: CuratedStackQuiz = {
  stoneColorMood: "ocean-cool",
  stoneEnergy: "classic-diamond",
  silhouetteVibes: ["classic-timeless"],
  shapePreferences: ["oval"],
  extraLevel: "subtle",
  bandPersonality: "sleek",
  metalTone: "yellow-gold",
  sizeVibe: "balanced",
};

const normalized = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const aliasTags: Record<string, string[]> = {
  round: ["round", "roundcut", "oldeuropeanround"],
  oval: ["oval", "ovalcut", "oldmineovalcut", "rosecutoval"],
  emerald: ["emerald", "emeraldcut", "oldmineemeraldcut", "stepcut"],
  cushion: ["cushion", "cushioncut", "squarecushioncut", "rectanglecushioncut", "oldminecushioncut"],
  pear: ["pear", "pearcut", "oldminepearcut"],
  princess: ["princess", "princesscut", "oldmineprincesscut"],
  radiant: ["radiant", "radiantcut", "oldmineradiantcut"],
  asscher: ["asscher", "asschercut"],
  marquise: ["marquise", "marquisecut", "oldminemarquisecut"],
  trillion: ["trillion", "trillioncut", "trillioncut", "trianglecut"],
  hexagon: ["hexagon", "hexagoncut", "hexagonoutline"],
  yellowgold: ["yellowgold", "gold", "14k", "18k", "10k"],
  whitegold: ["whitegold"],
  rosegold: ["rosegold"],
};

const COLOR_GROUPS: Record<CuratedColorMood, string[]> = {
  "ocean-cool": ["blue", "lightblue", "londonblue", "teal", "tealblue", "skyblue", "aquamarine", "bluegreen", "blueteal", "silveryblue"],
  "forest-green": ["green", "mint", "mintgreen", "greenmint", "darkgreen", "intensegreen", "emerald", "tsavorite", "peridot"],
  "sunset-warm": ["orange", "champagne", "mandarine", "yellow", "peach", "brown", "topaz", "sunstone", "turquoise"],
  "romantic-pink": ["pink", "softpink", "peachpink", "watermelon", "morganite", "pinkpurple", "peachypink"],
  "midnight-dark": ["black", "grey", "saltpepper", "onyx", "darkblue"],
  "rainbow-playful": ["rainbow", "multicolor", "violet", "purple", "greenwhite", "bluepurple"],
};

const ENERGY_GROUPS: Record<CuratedStoneEnergy, string[]> = {
  "classic-diamond": ["diamond", "diamonds", "brilliantcut", "roundcut", "solitaire"],
  "color-pop": ["sapphire", "ruby", "rubies", "emerald", "tourmaline", "tanzanite", "amethyst", "garnet", "spinel", "topaz"],
  "dreamy-soft": ["morganite", "moonstone", "opal", "quartz", "labradorite", "aquamarine"],
  "ethical-lab": ["labdiamond", "labalexandrite", "moissanite"],
  "dark-dramatic": ["onyx", "black", "saltpepper", "mossagate", "fireagate", "lapislazuli"],
};

const SILHOUETTE_GROUPS: Record<CuratedSilhouette, string[]> = {
  "classic-timeless": ["round", "oval", "cushion", "solitaire", "cathedralsetting", "fourprongsetting", "sixprongsetting"],
  "modern-clean": ["emerald", "asscher", "radiant", "bezelsetting", "halfbezelsetting", "easttowest", "knifeedgeshank"],
  "antique-romance": ["oldminecushioncut", "oldeuropeanround", "rosecut", "oldmineovalcut", "oldminepearcut", "vintage", "artdeco", "milgrain", "halosetting"],
  "bold-statement": ["pear", "marquise", "trillion", "kitecut", "hexagon", "cocktail", "clustersetting", "doublehalo", "threestone", "toietmoi"],
  "unique-artistic": ["portraitcut", "octagon", "lozengecut", "shieldcut", "spearcut", "organic", "twistedshank", "openring", "whimsical"],
};

const EXTRA_GROUPS: Record<CuratedExtraLevel, string[]> = {
  clean: ["solitaire", "bezelsetting", "halfbezelsetting", "flatshank"],
  subtle: ["bridgeaccent", "cathedralsetting", "scallopsetting", "halfroundshank", "taperedshank"],
  sparkly: ["halosetting", "hiddenshalo", "doublehalo", "pavesetting", "distancepavesetting", "clusterhalo"],
  statement: ["clustersetting", "cocktail", "threestone", "toietmoi", "sixtone", "fivestone"],
};

const BAND_GROUPS: Record<CuratedBandPersonality, string[]> = {
  sleek: ["flatshank", "halfroundshank", "taperedshank", "solitaire"],
  "twisted-organic": ["twistedshank", "organic", "ropeshank", "openring", "bypassshank"],
  "vintage-texture": ["milgrain", "vintage", "artdeco", "scallopsetting", "eternity", "halfeternity"],
  "geometric-edgy": ["knifeedgeshank", "spikedshank", "vshaped", "hexagonoutline", "octagonoutline", "cutoutshank"],
};

const METAL_GROUPS: Record<CuratedMetalTone, string[]> = {
  "yellow-gold": ["yellowgold", "gold", "14k", "18k", "10k"],
  "white-gold": ["whitegold"],
  "rose-gold": ["rosegold"],
  platinum: ["platinum"],
  silver: ["silver"],
};

const SIZE_GROUPS: Record<CuratedSizeVibe, { tags: string[]; sizes: number[] }> = {
  delicate: { tags: ["01carat", "1carat"], sizes: [5, 10] },
  balanced: { tags: ["1carat", "25carat"], sizes: [10, 15] },
  bold: { tags: ["25carat", "45carat"], sizes: [15, 20] },
};

const SHAPE_BASE_GROUPS: Record<string, string[]> = {
  round: ["round", "roundcut", "oldeuropeanround"],
  oval: ["oval", "ovalcut", "oldmineovalcut", "rosecutoval"],
  emerald: ["emerald", "emeraldcut", "oldmineemeraldcut", "stepcut", "stepcutoctagon"],
  cushion: ["cushion", "cushioncut", "squarecushioncut", "rectanglecushioncut", "oldminecushioncut"],
  pear: ["pear", "pearcut", "oldminepearcut"],
  princess: ["princess", "princesscut", "oldmineprincesscut"],
  radiant: ["radiant", "radiantcut", "oldmineradiantcut"],
  asscher: ["asscher", "asschercut"],
  marquise: ["marquise", "marquisecut", "oldminemarquisecut", "dutchmarquisecut"],
  trillion: ["trillion", "trianglecut", "trillioncut"],
  hexagon: ["hexagon", "hexagoncut", "hexagonoutline"],
};

const getRingTags = (ring: IRing): string[] => {
  const tags = Array.isArray(ring.stoneCut) ? ring.stoneCut : ring.stoneCut ? [ring.stoneCut] : [];
  return tags.map(normalized);
};

export const buildCuratedHistoryProfile = (
  likedRings: IRing[] = [],
  dislikedRings: IRing[] = [],
): CuratedHistoryProfile => {
  const likedTags: Record<string, number> = {};
  const dislikedTags: Record<string, number> = {};
  const sizeCounter = new Map<number, number>();

  likedRings.forEach((ring) => {
    getRingTags(ring).forEach((tag) => {
      likedTags[tag] = (likedTags[tag] || 0) + 1;
    });
    sizeCounter.set(ring.mainStoneSize, (sizeCounter.get(ring.mainStoneSize) || 0) + 1);
  });

  dislikedRings.forEach((ring) => {
    getRingTags(ring).forEach((tag) => {
      dislikedTags[tag] = (dislikedTags[tag] || 0) + 1;
    });
  });

  const preferredSizes = Array.from(sizeCounter.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([size]) => size);

  return {
    likedTags,
    dislikedTags,
    preferredSizes,
  };
};

const groupMatchScore = (ringTags: string[], groupTags: string[], weight: number) => {
  const matchCount = groupTags.filter((tag) => ringTags.includes(normalized(tag))).length;
  if (!matchCount) return 0;
  return Math.min(weight, Math.log2(matchCount + 1) * (weight / 2.2) + weight * 0.25);
};

const shapeGroupsFromQuiz = (shapePreferences: string[]) => {
  const selected = shapePreferences.slice(0, 2);
  return selected.map((shape) => SHAPE_BASE_GROUPS[shape] || [shape]);
};

const silhouetteGroupsFromQuiz = (silhouetteVibes: CuratedSilhouette[]) => {
  const selected = silhouetteVibes.slice(0, 2);
  return selected.map((vibe) => SILHOUETTE_GROUPS[vibe] || []);
};

const ringScore = (ring: IRing, quiz: CuratedStackQuiz, historyProfile?: CuratedHistoryProfile) => {
  const ringTags = getRingTags(ring);
  let score = 0;

  score += groupMatchScore(ringTags, COLOR_GROUPS[quiz.stoneColorMood], 5);
  score += groupMatchScore(ringTags, ENERGY_GROUPS[quiz.stoneEnergy], 4);
  score += groupMatchScore(ringTags, EXTRA_GROUPS[quiz.extraLevel], 3);
  score += groupMatchScore(ringTags, BAND_GROUPS[quiz.bandPersonality], 2.5);
  score += groupMatchScore(ringTags, METAL_GROUPS[quiz.metalTone], 2.5);

  shapeGroupsFromQuiz(quiz.shapePreferences).forEach((group, idx) => {
    score += groupMatchScore(ringTags, group, idx === 0 ? 4 : 2.6);
  });

  silhouetteGroupsFromQuiz(quiz.silhouetteVibes).forEach((group, idx) => {
    score += groupMatchScore(ringTags, group, idx === 0 ? 3 : 2);
  });

  const sizeInfo = SIZE_GROUPS[quiz.sizeVibe];
  score += groupMatchScore(ringTags, sizeInfo.tags, 1.3);
  if (sizeInfo.sizes.includes(ring.mainStoneSize)) score += 1.7;

  if (historyProfile) {
    const ringTags = getRingTags(ring);
    const positive = ringTags.reduce((acc, tag) => acc + Math.min(1.5, (historyProfile.likedTags[tag] || 0) * 0.28), 0);
    const negative = ringTags.reduce((acc, tag) => acc + Math.min(1.8, (historyProfile.dislikedTags[tag] || 0) * 0.36), 0);
    score += Math.min(3.5, positive);
    score -= Math.min(3.2, negative);

    if (historyProfile.preferredSizes.includes(ring.mainStoneSize)) {
      score += 1.1;
    }
  }

  if (typeof ring.rank === "number") {
    score += Math.max(0, 1.6 - ring.rank / 6500);
  }

  return score;
};

const getQuizPrimaryTags = (quiz: CuratedStackQuiz) => {
  const tags = [
    ...COLOR_GROUPS[quiz.stoneColorMood],
    ...ENERGY_GROUPS[quiz.stoneEnergy],
    ...EXTRA_GROUPS[quiz.extraLevel],
    ...BAND_GROUPS[quiz.bandPersonality],
    ...METAL_GROUPS[quiz.metalTone],
    ...shapeGroupsFromQuiz(quiz.shapePreferences).flat(),
    ...silhouetteGroupsFromQuiz(quiz.silhouetteVibes).flat(),
  ];
  return new Set(tags.map(normalized));
};

const pickWithDiversity = (
  ranked: { ring: IRing; score: number; tags: string[] }[],
  desiredCount: number,
  selectedIds: Set<string>,
  primaryTags: Set<string>,
) => {
  const picked: string[] = [];
  const localTagCounts = new Map<string, number>();

  for (let i = 0; i < ranked.length && picked.length < desiredCount; i += 1) {
    const candidate = ranked[i];
    if (!candidate.ring.id || selectedIds.has(candidate.ring.id)) continue;

    const overlapTags = candidate.tags.filter((tag) => primaryTags.has(tag));
    const penalty = overlapTags.reduce((acc, tag) => acc + Math.max(0, (localTagCounts.get(tag) || 0) - 2) * 0.55, 0);
    const adjusted = candidate.score - penalty;

    if (adjusted < 0.5) continue;

    selectedIds.add(candidate.ring.id);
    picked.push(candidate.ring.id);
    overlapTags.forEach((tag) => localTagCounts.set(tag, (localTagCounts.get(tag) || 0) + 1));
  }

  return picked;
};

export const isCuratedStackExpired = (generatedAt?: number | null) => {
  if (!generatedAt) return true;
  return Date.now() - generatedAt > CURATED_STACK_TTL_MS;
};

export const getLocalCuratedStack = (): CuratedStackRecordLocal | null => {
  try {
    const value = localStorage.getItem(CURATED_STACK_LOCAL_STORAGE_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value) as CuratedStackRecordLocal;
    if (!parsed?.quiz || !Array.isArray(parsed?.ringIds)) return null;

    return parsed;
  } catch (error) {
    console.error("Could not parse local curated stack", error);
    return null;
  }
};

export const setLocalCuratedStack = (stack: CuratedStackRecordLocal) => {
  localStorage.setItem(CURATED_STACK_LOCAL_STORAGE_KEY, JSON.stringify(stack));
};

export const clearLocalCuratedStack = () => {
  localStorage.removeItem(CURATED_STACK_LOCAL_STORAGE_KEY);
};

export const setCuratedOnboardingPending = () => {
  localStorage.setItem(CURATED_STACK_ONBOARDING_PENDING_KEY, "1");
};

export const isCuratedOnboardingPending = () => {
  return localStorage.getItem(CURATED_STACK_ONBOARDING_PENDING_KEY) === "1";
};

export const clearCuratedOnboardingPending = () => {
  localStorage.removeItem(CURATED_STACK_ONBOARDING_PENDING_KEY);
};

export const buildWhyThisFitsNote = (ring: IRing, quiz: CuratedStackQuiz) => {
  const ringTags = getRingTags(ring);
  const reasons: string[] = [];

  if (COLOR_GROUPS[quiz.stoneColorMood].some((tag) => ringTags.includes(normalized(tag)))) {
    reasons.push("Matches your color mood");
  }
  if (ENERGY_GROUPS[quiz.stoneEnergy].some((tag) => ringTags.includes(normalized(tag)))) {
    reasons.push("Fits your stone energy");
  }
  if (silhouetteGroupsFromQuiz(quiz.silhouetteVibes).some((group) => group.some((tag) => ringTags.includes(normalized(tag))))) {
    reasons.push("Aligned with your silhouette vibe");
  }
  if (shapeGroupsFromQuiz(quiz.shapePreferences).some((group) => group.some((tag) => ringTags.includes(normalized(tag))))) {
    reasons.push("Matches preferred stone shape");
  }
  if (METAL_GROUPS[quiz.metalTone].some((tag) => ringTags.includes(normalized(tag)))) {
    reasons.push("Metal tone match");
  }
  if (EXTRA_GROUPS[quiz.extraLevel].some((tag) => ringTags.includes(normalized(tag)))) {
    reasons.push("Right level of detail");
  }

  return reasons.slice(0, 3);
};

export const generateCuratedStackIds = (
  allRings: IRing[],
  quiz: CuratedStackQuiz,
  stackSize = CURATED_STACK_SIZE,
  historyProfile?: CuratedHistoryProfile,
) => {
  const primaryTags = getQuizPrimaryTags(quiz);

  const scored = allRings
    .map((ring) => ({ ring, tags: getRingTags(ring), score: ringScore(ring, quiz, historyProfile) }))
    .filter((item) => item.ring?.id)
    .sort((a, b) => b.score - a.score);

  const strictPool = scored.filter((item) => item.score >= 8);
  const adjacentPool = scored.filter((item) => item.score >= 4.5 && item.score < 8);
  const explorationPool = scored.filter((item) => item.score < 4.5);

  const selectedIds = new Set<string>();

  const strictTarget = Math.min(7, stackSize);
  const adjacentTarget = Math.min(2, Math.max(0, stackSize - strictTarget));
  const explorationTarget = Math.max(0, stackSize - strictTarget - adjacentTarget);

  const strictPicked = pickWithDiversity(strictPool, strictTarget, selectedIds, primaryTags);
  const adjacentPicked = pickWithDiversity(adjacentPool, adjacentTarget, selectedIds, primaryTags);

  const explorationPicked: string[] = [];
  for (const item of explorationPool) {
    if (explorationPicked.length >= explorationTarget) break;
    if (!item.ring.id || selectedIds.has(item.ring.id)) continue;
    selectedIds.add(item.ring.id);
    explorationPicked.push(item.ring.id);
  }

  const result = [...strictPicked, ...adjacentPicked, ...explorationPicked];

  if (result.length < stackSize) {
    for (const item of scored) {
      if (result.length >= stackSize) break;
      if (!item.ring.id || selectedIds.has(item.ring.id)) continue;
      selectedIds.add(item.ring.id);
      result.push(item.ring.id);
    }
  }

  return result.slice(0, stackSize);
};

export const mergeCuratedStacks = (
  localStack: CuratedStackRecordLocal,
  remoteStack?: CuratedStackRecordLocal | null,
): CuratedStackRecordLocal => {
  if (!remoteStack || !Array.isArray(remoteStack.ringIds)) {
    return localStack;
  }

  const remoteGeneratedAt = remoteStack.generatedAt || 0;
  const localGeneratedAt = localStack.generatedAt || 0;

  if (localGeneratedAt > remoteGeneratedAt) return localStack;
  if (remoteGeneratedAt > localGeneratedAt) return remoteStack;

  const mergedIds = Array.from(new Set([...localStack.ringIds, ...remoteStack.ringIds])).slice(0, CURATED_STACK_SIZE);
  return {
    quiz: localStack.quiz,
    generatedAt: localGeneratedAt,
    ringIds: mergedIds,
  };
};

export const getCuratedSummaryTraits = (quiz: CuratedStackQuiz) => {
  const metalLabel = CURATED_METAL_OPTIONS.find((option) => option.value === quiz.metalTone)?.label;
  const colorLabel = CURATED_COLOR_MOOD_OPTIONS.find((option) => option.value === quiz.stoneColorMood)?.label;

  return [
    `Color mood: ${colorLabel || quiz.stoneColorMood}`,
    `Stone energy: ${quiz.stoneEnergy}`,
    `Metal: ${metalLabel || quiz.metalTone}`,
  ];
};
